import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Users, 
  GraduationCap, 
  Heart, 
  UserCheck, 
  Star,
  Clock,
  ArrowRight,
  AlertCircle
} from 'lucide-react';

const FormsPage = () => {
  const forms = [
    {
      id: 'student-needs-assessment',
      title: 'Student Needs Assessment Form',
      description: 'Help us understand your academic, personal, and social needs for better support.',
      icon: Users,
      estimatedTime: '10-15 minutes',
      category: 'Assessment',
      priority: 'recommended',
      whoShouldComplete: 'All new students and those seeking guidance services',
      link: '/forms/student-needs-assessment'
    },
    {
      id: 'exit-interview',
      title: 'Exit Interview Form',
      description: 'Graduate feedback and assessment to help us improve our services.',
      icon: GraduationCap,
      estimatedTime: '15-20 minutes',
      category: 'Graduation',
      priority: 'required',
      whoShouldComplete: 'All graduating students',
      link: '/forms/exit-interview'
    },
    {
      id: 'hed-exit-interview',
      title: 'HED Exit Interview Form',
      description: 'Comprehensive exit interview for HED graduating students with detailed assessment.',
      icon: GraduationCap,
      estimatedTime: '20-25 minutes',
      category: 'Graduation',
      priority: 'required',
      whoShouldComplete: 'All HED graduating students',
      link: '/forms/hed-exit-interview'
    },
    {
      id: 'intake-interview',
      title: 'Intake Interview Form',
      description: 'First-visit assessment for new counseling clients.',
      icon: UserCheck,
      estimatedTime: '20-25 minutes',
      category: 'Counseling',
      priority: 'required',
      whoShouldComplete: 'Students beginning counseling services',
      link: '/forms/intake-interview'
    },
    {
      id: 'counseling-referral',
      title: 'Counseling Referral Form',
      description: 'Staff referral form for students who may benefit from counseling services.',
      icon: Heart,
      estimatedTime: '5-10 minutes',
      category: 'Referral',
      priority: 'staff-only',
      whoShouldComplete: 'Faculty and staff members only',
      link: '/forms/counseling-referral'
    },
    {
      id: 'senior-high-exit',
      title: 'Senior High School Exit Form',
      description: 'Post-secondary planning assessment for graduating senior high students.',
      icon: GraduationCap,
      estimatedTime: '15-20 minutes',
      category: 'Graduation',
      priority: 'required',
      whoShouldComplete: 'All Grade 12 students',
      link: '/forms/senior-high-exit'
    },
    {
      id: 'faculty-evaluation',
      title: 'Faculty Evaluation Form',
      description: 'Faculty feedback on guidance services and program effectiveness.',
      icon: Star,
      estimatedTime: '10-15 minutes',
      category: 'Evaluation',
      priority: 'staff-only',
      whoShouldComplete: 'Faculty and staff members',
      link: '/forms/faculty-evaluation'
    },
    {
      id: 'services-evaluation',
      title: 'Services Evaluation Form',
      description: 'Student satisfaction survey for guidance services received.',
      icon: Star,
      estimatedTime: '5-10 minutes',
      category: 'Evaluation',
      priority: 'optional',
      whoShouldComplete: 'Students who have used guidance services',
      link: '/forms/services-evaluation'
    }
  ];

  const categories = ['All', 'Assessment', 'Counseling', 'Graduation', 'Referral', 'Evaluation'];
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const filteredForms = selectedCategory === 'All' 
    ? forms 
    : forms.filter(form => form.category === selectedCategory);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'required': return 'bg-blue-100 text-blue-800';
      case 'recommended': return 'bg-yellow-100 text-yellow-800';
      case 'optional': return 'bg-green-100 text-green-800';
      case 'staff-only': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'required': return 'Required';
      case 'recommended': return 'Recommended';
      case 'optional': return 'Optional';
      case 'staff-only': return 'Staff Only';
      default: return priority;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-16">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-6">Guidance Forms</h1>
            <p className="text-xl text-blue-100">
              Complete the appropriate forms to help us provide you with the best possible 
              guidance and support services.
            </p>
          </div>
        </div>
      </section>

      {/* Instructions */}
      <section className="py-8 bg-blue-50 border-b">
        <div className="container-custom">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-blue-600 mr-4 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Before You Begin</h3>
              <ul className="text-blue-700 space-y-1 text-sm">
                <li>• All forms are confidential and secure</li>
                <li>• You can save your progress and return later</li>
                <li>• Required forms must be completed for certain services</li>
                <li>• Contact us if you need assistance completing any form</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-800 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Forms Grid */}
      <section className="pb-16">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredForms.map((form) => (
              <div key={form.id} className="card-hover group">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <form.icon className="w-6 h-6 text-blue-800" />
                  </div>
                  <span className={`status-badge ${getPriorityColor(form.priority)}`}>
                    {getPriorityText(form.priority)}
                  </span>
                </div>

                <h3 className="text-lg font-semibold mb-3 text-gray-800 group-hover:text-blue-800 transition-colors">
                  {form.title}
                </h3>

                <p className="text-gray-600 mb-4 text-sm">
                  {form.description}
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 mr-2" />
                    {form.estimatedTime}
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-1">Who should complete:</p>
                    <p className="text-xs text-gray-600">{form.whoShouldComplete}</p>
                  </div>
                </div>

                <Link
                  to={form.link}
                  className="btn-primary w-full flex items-center justify-center group-hover:bg-red-900 transition-colors"
                >
                  Complete Form
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            ))}
          </div>

          {filteredForms.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No forms found</h3>
              <p className="text-gray-500">Try selecting a different category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">Need Help?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              If you have questions about which forms to complete or need assistance, 
              we're here to help.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-3">Form Questions</h3>
              <p className="text-gray-600 text-sm mb-4">
                Not sure which form to complete? Contact us for guidance on the right forms for your situation.
              </p>
              <Link to="/contact" className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                Get Help Choosing →
              </Link>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-3">Technical Support</h3>
              <p className="text-gray-600 text-sm mb-4">
                Having trouble with the online forms? We can help you complete them or provide paper versions.
              </p>
              <Link to="/contact" className="text-green-600 hover:text-green-700 font-medium text-sm">
                Get Technical Help →
              </Link>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-3">Urgent Needs</h3>
              <p className="text-gray-600 text-sm mb-4">
                If you need immediate support or are in crisis, don't wait for forms. Contact us right away.
              </p>
              <Link to="/contact" className="text-purple-600 hover:text-purple-700 font-medium text-sm">
                Get Immediate Help →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Notice */}
      <section className="py-8 bg-gray-100">
        <div className="container-custom">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Privacy & Confidentiality</h3>
            <p className="text-gray-600 text-sm max-w-3xl mx-auto">
              All information provided in these forms is kept strictly confidential in accordance with 
              professional counseling standards and school policies. Information is only shared with 
              appropriate school personnel on a need-to-know basis for your educational benefit and safety.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FormsPage;